import { Table } from '@startuptools/ui/Table';
import { Alert } from '@startuptools/ui/Alert';
import { ModalDialog } from '@startuptools/ui/ModalDialog';
import { Modal } from '@startuptools/ui/Modal';
import { ModalClose } from '@startuptools/ui/ModalClose';
import { Typography } from '@startuptools/ui/Typography';
import { Skeleton } from '@startuptools/ui/Skeleton';
import { Stack } from '@startuptools/ui/Stack';
import { Box } from '@startuptools/ui/Box';
import DialogContent from '@mui/joy/DialogContent';
import { useSnrCasesSuspenseQuery } from '../../graphql/react-operations';
import { useCompanyId } from '@startuptools/common/react-wrapper';
import { type GqlSnrCase, type GqlSnrCaseSubjectKind } from '../../graphql/base-types.graphql';
import { Trans, useLingui } from '@lingui/react';
import { DateFormat } from '@startuptools/ui/DateFormat';
import { DateTime } from 'luxon';
import { Suspense, type ComponentProps } from 'react';

export type ConnectSnrCaseDialogProps = ComponentProps<typeof ConnectSnrCaseDialog>;

export const ConnectSnrCaseDialog = ({ onClose, ...props }: ComponentProps<typeof ConnectSnrCaseDialogContent>) => {
  return (
    <Modal open onClose={onClose}>
      <ModalDialog>
        <ModalClose />
        <Box mt={3}>
          <Suspense
            fallback={
              <DialogContent>
                <Stack gap={1} minWidth={700}>
                  <Skeleton variant="rectangular" height={36} />
                  <Skeleton variant="rectangular" height={36} />
                  <Skeleton variant="rectangular" height={36} />
                </Stack>
              </DialogContent>
            }
          >
            <ConnectSnrCaseDialogContent {...props} onClose={onClose} />
          </Suspense>
        </Box>
      </ModalDialog>
    </Modal>
  );
};

const ConnectSnrCaseDialogContent = ({
  subjectKinds,
  fromDate,
  onClick,
  onClose,
}: {
  subjectKinds: GqlSnrCaseSubjectKind[];
  fromDate: string;
  onClick: (snrCase: Pick<GqlSnrCase, 'id'>) => Promise<unknown>;
  onClose: () => void;
}) => {
  const companyId = useCompanyId();
  const { i18n } = useLingui();
  const {
    data: { snrCases },
  } = useSnrCasesSuspenseQuery({ variables: { companyId } });
  const relevantCases = snrCases.models.filter(c => {
    // filtering out those that do not have arrival date set is safe, since they are from 1990 or before.
    return (
      c.arrivalDate &&
      DateTime.fromISO(c.arrivalDate).startOf('day').toMillis() >=
        DateTime.fromISO(fromDate).startOf('day').toMillis() &&
      c.subjects.some(s => subjectKinds.some(k => k === s.kind))
    );
  });

  const handleOnClick = (snrCase: Pick<GqlSnrCase, 'id'>) => async () => {
    await onClick(snrCase);
    onClose();
  };

  return (
    <>
      {relevantCases.length > 0 ? (
        <Table sx={{ tableLayout: 'auto' }} hoverRow>
          <thead>
            <tr>
              <th>
                <Trans id="Registered At" />
              </th>
              <th>
                <Trans id="Arrived At" />
              </th>
              <th>
                <Trans id="Subject" />
              </th>
              <th>
                <Trans id="Status" />
              </th>
              <th>
                <Trans id="Filing Number" />
              </th>
            </tr>
          </thead>
          <tbody>
            {relevantCases.map(c => (
              <tr key={c.id} onClick={handleOnClick(c)} style={{ cursor: 'pointer' }}>
                <td>
                  <DateFormat locale={i18n.locale} format={DateTime.DATE_SHORT}>
                    {c.registrationDate}
                  </DateFormat>
                </td>
                <td>
                  <DateFormat locale={i18n.locale} format={DateTime.DATE_SHORT}>
                    {c.arrivalDate}
                  </DateFormat>
                </td>
                <td>
                  {c.subjects.map(s => (
                    <Typography key={s.kind}>{s.value}</Typography>
                  ))}
                </td>
                <td>
                  {c.status.map(s => (
                    <Typography key={s.kod}>{s.value}</Typography>
                  ))}
                </td>
                <td>
                  {c.filingNumber}/{c.filingYear}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      ) : (
        <Alert>
          <Trans id="We could not find any suitable cases at Bolagsverket. If you believe this is an error please contact support" />
        </Alert>
      )}
    </>
  );
};
